import {Callout} from '@wandb/weave/components/Callout';
import {WaveLoader} from '@wandb/weave/components/Loaders/WaveLoader';
import {Tailwind} from '@wandb/weave/components/Tailwind';
import React, {useEffect, useMemo, useRef} from 'react';
import {usePrevious} from 'react-use';

import {useDeepMemo} from '../../../../../../hookUtils';
import {Button} from '../../../../../Button';
import {IconName} from '../../../../../Icon';
import {useEntityProject} from '../../context';
import {CustomWeaveTypeProjectContext} from '../../typeViews/CustomWeaveTypeDispatcher';
import {usePlaygroundContext} from '../PlaygroundPage/PlaygroundContext';
import {ChoicesView} from './ChoicesView';
import {
  MarkdownSettingProvider,
  useMarkdownSetting,
} from './MarkdownSettingContext';
import {MessageList} from './MessageList';
import {MessagePanel} from './MessagePanel';
import {Chat} from './types';

type ChatViewProps = {
  chat: Chat;
  showTitle?: boolean;
  autoScroll?: boolean;
};

const ChatViewInner = ({chat, showTitle = true, autoScroll}: ChatViewProps) => {
  const outputRef = useRef<HTMLDivElement>(null);
  const playgroundContext = usePlaygroundContext();
  const {showMarkdownInChatView, setShowMarkdownInChatView} =
    useMarkdownSetting();

  const chatResult = useDeepMemo(chat.result);

  // Determine if auto-scroll should be enabled
  const shouldAutoScroll = autoScroll ?? playgroundContext.isPlayground;

  // Check if we should show loading state
  const shouldShowLoading = useMemo(() => {
    if (!playgroundContext.isPlayground) {
      return false;
    }

    const messages = chat.request?.messages || [];
    if (messages.length === 0) {
      return false;
    }

    return playgroundContext.isStreaming;
  }, [
    playgroundContext.isPlayground,
    playgroundContext.isStreaming,
    chat.request?.messages,
  ]);

  // Shouldnt happen, but if the user sends an empty message
  // Show a response panel if there is content, tool calls, or reasoning
  const showContent =
    !!chatResult &&
    ((('content' in chatResult &&
      chatResult.content &&
      (Array.isArray(chatResult.content)
        ? chatResult.content.length > 0
        : true)) as boolean) ||
      (('tool_calls' in chatResult &&
        (chatResult as any).tool_calls &&
        (chatResult as any).tool_calls.length > 0) as boolean) ||
      (('reasoning_content' in chatResult &&
        (chatResult as any).reasoning_content &&
        (chatResult as any).reasoning_content.length > 0) as boolean));

  const showChoices =
    chatResult &&
    'choices' in chatResult &&
    chatResult.choices &&
    chatResult.choices.length > 0;

  const scrollLastMessage = useMemo(
    () =>
      shouldAutoScroll &&
      !(
        outputRef.current &&
        chatResult &&
        'choices' in chatResult &&
        chatResult.choices
      ),
    [chatResult, shouldAutoScroll]
  );

  const currentMessageCount = chat.request?.messages?.length || 0;
  const previousMessageCount = usePrevious(currentMessageCount) || 0;

  const shouldShowPrompt = useMemo(() => {
    return (
      !playgroundContext.isPlayground &&
      chat?.prompt?.messages?.length &&
      chat?.prompt?.messages?.length > 0
    );
  }, [playgroundContext.isPlayground, chat?.prompt?.messages]);

  useEffect(() => {
    const hasNewMessage = currentMessageCount > previousMessageCount;

    if (hasNewMessage && outputRef.current && shouldAutoScroll) {
      outputRef.current.scrollIntoView();
    }
  }, [currentMessageCount, previousMessageCount, shouldAutoScroll]);

  const content = (
    <>
      {showTitle && (
        <div className="mb-[8px] flex items-center justify-between">
          <p className="text-sm font-semibold text-moon-800">
            {shouldShowPrompt ? 'Prompt' : 'Messages'}
          </p>
          {!playgroundContext.isPlayground && (
            <div className="flex items-center gap-4">
              <Button
                size="small"
                variant={showMarkdownInChatView ? 'secondary' : 'ghost'}
                startIcon={'markdown' as IconName}
                aria-label="Render as Markdown"
                onClick={() => setShowMarkdownInChatView(true)}
              />
              <Button
                size="small"
                variant={!showMarkdownInChatView ? 'secondary' : 'ghost'}
                startIcon={'code-alt' as IconName}
                aria-label="Render as plain text"
                onClick={() => setShowMarkdownInChatView(false)}
              />
            </div>
          )}
        </div>
      )}
      {/* Show prompt messages if present (only in non-playground views) */}
      {shouldShowPrompt && (
        <>
          <MessageList
            messages={chat?.prompt?.messages ?? []}
            scrollLastMessage={false}
          />
          {/* Divider between prompt and messages */}
          <div className="mt-4 mb-8 border-t border-moon-250 " />
          <div className="mb-[8px] flex items-center justify-between">
            <p className="text-sm font-semibold text-moon-800">Messages</p>
          </div>
        </>
      )}
      <MessageList
        messages={chat.request?.messages || []}
        scrollLastMessage={scrollLastMessage}
      />
      {/* Show loading state when waiting for response */}
      {shouldShowLoading && !showContent && !showChoices && (
        <>
          {showTitle && (
            <span className="mb-[8px] text-sm font-semibold text-moon-800">
              Response
            </span>
          )}
          <div className="flex gap-[16px] py-4">
            <div className="w-32 flex-shrink-0">
              <Callout
                size="x-small"
                icon="robot-service-member"
                color="moon"
                className="mt-[4px] h-32 w-32 bg-moon-100"
              />
            </div>
            <div className="flex items-center">
              <WaveLoader size="small" />
            </div>
          </div>
        </>
      )}
      {showContent && !('choices' in chatResult) && (
        <>
          <span className="mb-[8px] text-sm font-semibold text-moon-800">
            Response
          </span>
          <div ref={outputRef}>
            {'content' in chatResult && Array.isArray(chatResult.content) ? (
              chatResult.content
                .filter(p => p != null)
                .map((part, idx) => (
                  <div key={idx} className={idx > 0 ? 'mt-4' : ''}>
                    <MessagePanel
                      index={idx}
                      message={{
                        ...chatResult,
                        content: part as any,
                        // only show reasoning/tool_calls on first bubble
                        reasoning_content:
                          idx === 0
                            ? (chatResult as any).reasoning_content
                            : undefined,
                        tool_calls:
                          idx === 0
                            ? (chatResult as any).tool_calls
                            : undefined,
                      }}
                      isStructuredOutput={chat.isStructuredOutput}
                      isNested={false}
                      choiceIndex={0}
                      messageHeader={null}
                    />
                  </div>
                ))
            ) : (
              <MessagePanel
                index={0}
                message={chatResult}
                isStructuredOutput={chat.isStructuredOutput}
                isNested={false}
                choiceIndex={0}
                messageHeader={null}
              />
            )}
          </div>
        </>
      )}
      {showChoices && (
        <>
          {showTitle && (
            <span className="mb-[8px] text-sm font-semibold text-moon-800">
              Response
            </span>
          )}
          <div ref={outputRef}>
            <ChoicesView
              isStructuredOutput={chat.isStructuredOutput}
              choices={chatResult.choices}
            />
          </div>
        </>
      )}
    </>
  );

  const {entity, project} = useEntityProject();

  // Wrap with CustomWeaveTypeProjectContext if entity and project are provided
  if (entity && project) {
    return (
      <div className="flex flex-col pb-32">
        <CustomWeaveTypeProjectContext.Provider
          value={{entity, project, mode: 'chat'}}>
          {content}
        </CustomWeaveTypeProjectContext.Provider>
      </div>
    );
  }

  return <div className="flex flex-col pb-32">{content}</div>;
};

export const ChatView = ({
  chat,
  showTitle = true,
  autoScroll,
}: ChatViewProps) => {
  if (!chat.isValid) {
    return (
      <Tailwind>
        <div className="mr-auto flex w-full whitespace-nowrap font-semibold text-sm text-moon-600">
          Chat format is invalid, cannot render.
        </div>
      </Tailwind>
    );
  }

  return (
    <MarkdownSettingProvider>
      <ChatViewInner
        chat={chat}
        showTitle={showTitle}
        autoScroll={autoScroll}
      />
    </MarkdownSettingProvider>
  );
};
